// Вкладка «Расчёт сотрудника»: документ по одному человеку за несколько месяцев и объектов.
//
// ## Откуда взялась
//
// Финансовый директор заказчика 29.09.2026 попросил в кабинете «Рассчитать сотрудника» для
// увольняющегося человека: за одно действие получить расчёт по всем месяцам и объектам, где он
// работал, «как в образце Расчёт Шаврин». Раньше единицей денег была ведомость «объект × месяц», и
// такой расчёт сводили руками в Excel.
//
// ## Как устроено
//
// Документ сводный: он только читает ведомости. Деньги двигаются там же, где и раньше, —
// утверждением ведомости и отметкой выплаты в строке человека. Всё посчитал и сложил сервер.
//
// Человек выбран — документ строится СРАЗУ, без кнопки «Рассчитать»: выбор и есть действие. Человек
// и границы периода живут в адресе (`?f.employee=…&f.from=2025-11&f.to=2026-03`): ссылка на расчёт
// открывается у коллеги тем же расчётом. Границы не заданы — период выбирает сервер: от первой
// ведомости человека до месяца увольнения.
//
// ## Что видно во времени (путь человека)
//
// 0 мс: рамка вкладки, поле выбора либо скелет документа под шапкой с календарями. Дольше трёх
// секунд: под скелетом сказано, что связь медленная, а не просто «ничего нет». Листают месяц:
// прежний документ остаётся на месте приглушённым, пока едет новый, и подпись над таблицей
// называет тот период, который на экране; другому человеку прежние деньги не показываются ни на
// секунду (`queries.ts`). Обрыв связи: что не приехало и «Повторить»; если документ уже был,
// остаётся он, а над ним сказано, что обновить не удалось. Нет ведомостей: причина и шаг. Чужой
// человек: «такого человека нет или он вам не виден», а не голый отказ. Нет права: вкладки нет,
// а по прямой ссылке объяснение, кто видит расчёт.

import { useEffect, useState } from 'react';

import { Link, useLocation, useNavigate } from 'react-router';

import {
  RiArrowLeftRightLine,
  RiCalculatorLine,
  RiErrorWarningLine,
  RiLockLine,
  RiUserUnfollowLine,
} from '@remixicon/react';

import type { PayslipPeriodOut } from '@/shared/api/endpoints/payslip';
import { useAuthStore } from '@/shared/auth/store';
import { ApiError } from '@/shared/errors/api-error';
import { can } from '@/shared/rbac/permissions';
import type { SearchState } from '@/shared/routing/search';
import { buildSearch } from '@/shared/routing/search';
import type { Period } from '@/shared/time/period';
import {
  formatPeriodKey,
  formatPeriodTitle,
  месяцИзАдреса,
  parsePeriod,
} from '@/shared/time/period';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { EmptyState, GridSkeleton, ScreenState, Skeleton } from '@/shared/ui/screen-state';
import { Toolbar, ToolbarField } from '@/shared/ui/toolbar';
import { cn } from '@/utils/cn';

import { FILTER_EMPLOYEE, FILTER_FROM, FILTER_TO } from '../route';

import { Документ } from './Документ';
import { ВыборЧеловека } from './ВыборЧеловека';
import { useРасчётСотрудника, useЧеловек } from './queries';
import { вспомнитьЧеловека, запомнитьЧеловека } from './запомнить';
import type { Граница } from './тексты';
import { конецПериода, началоПериода, шагГраницы } from './тексты';

/** Расчёт собирается из ведомостей, и видит его тот, кто видит ведомости. Сервер проверяет то же. */
const ПРАВО = 'core.payroll.view';

const АДРЕС_ВЫБОРА = '/payroll/settlement';

/** Через сколько скелет признаётся долгим и объясняет, что дело в связи. */
const ДОЛГО_МС = 3000;

export function SettlementScreen({ search }: { search: SearchState }) {
  const права = useAuthStore((состояние) => состояние.permissions);
  const сотрудник = search.filters[FILTER_EMPLOYEE] ?? null;
  const [последний] = useState(вспомнитьЧеловека);

  if (!can(права, ПРАВО)) {
    return <НетПрава />;
  }
  if (сотрудник === null) {
    return (
      <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-weak-50'>
        <ВыборЧеловека последний={последний} />
      </main>
    );
  }
  // Ключ по человеку: другой человек это другой экран, и прежнее объяснение запрета шага или
  // прежний документ ему не принадлежат.
  return <РасчётЧеловека key={сотрудник} search={search} сотрудник={сотрудник} />;
}

function РасчётЧеловека({ search, сотрудник }: { search: SearchState; сотрудник: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const отАдреса = месяцИзАдреса(search.filters[FILTER_FROM]);
  const доАдреса = месяцИзАдреса(search.filters[FILTER_TO]);
  const документ = useРасчётСотрудника(сотрудник, отАдреса, доАдреса);
  // Имя для шапки берётся и из карточки человека: она лежит в кэше, когда расчёт открыли из его
  // карточки, и имя стоит на месте с первого кадра, а при ошибке расчёта остаётся: человек должен
  // видеть, ЧЕЙ расчёт не загрузился. Ключ запроса общий с карточкой в разделе «Люди».
  const карточка = useЧеловек(сотрудник);
  const данные = документ.data;
  const период = данные?.period ?? null;
  const [запрет, setЗапрет] = useState<string | null>(null);

  // Календари показывают то, что ЗАПРОШЕНО: граница из адреса главнее границы из ответа. Пока едет
  // новый запрос, в ответе лежит прежний период, и два нажатия стрелки подряд двигали бы границу
  // только на один месяц. Нет в адресе — берётся то, что выбрал сервер.
  const от = отАдреса ?? (период === null ? null : началоПериода(период));
  const до = доАдреса ?? (период === null ? null : конецПериода(период));

  const человекаНет = документ.error instanceof ApiError && документ.error.status === 404;
  const имя = данные?.person.name ?? карточка.data?.display_name ?? null;
  // Календари нужны, пока едет запрос, и когда у документа есть период. При ошибке без документа
  // листать нечего: человеку остаётся «Повторить» либо другой человек. Когда ведомостей у человека
  // нет вовсе, периода тоже нет, и календарь с вечной заглушкой «определяем месяц» врал бы.
  const календариВидны = данные === undefined ? !документ.isError : период !== null;

  // Человек запоминается, только когда документ по нему приехал: несуществующий идентификатор из
  // чужой ссылки «вернуться к расчёту» не предлагал бы.
  useEffect(() => {
    if (данные !== undefined) {
      запомнитьЧеловека(сотрудник);
    }
  }, [данные, сотрудник]);

  const сегодня = parsePeriod(undefined);

  function сдвинуть(какая: Граница, шаг: -1 | 1) {
    if (от === null || до === null) {
      setЗапрет('Период ещё определяется: подождите, пока расчёт построится.');
      return;
    }
    const итог = шагГраницы(какая, шаг, от, до, сегодня);
    if ('запрет' in итог) {
      setЗапрет(итог.запрет);
      return;
    }
    setЗапрет(null);
    void navigate({
      pathname: location.pathname,
      search: buildSearch({
        ...search,
        filters: {
          ...search.filters,
          [какая === 'от' ? FILTER_FROM : FILTER_TO]: formatPeriodKey(итог.период),
        },
        panels: [],
      }),
    });
  }

  // Шаг вперёд у начала упирается в конец, а у конца в текущий месяц: об этом сказано в подписи
  // кнопки для чтения с экрана, а не только после нажатия. Слова те же, что у объяснения запрета.
  const причинаВперёдОт = причина(от, до, 'от', сегодня);
  const причинаВперёдДо = причина(от, до, 'до', сегодня);

  return (
    <main className='flex min-h-0 min-w-0 flex-1 flex-col bg-bg-weak-50'>
      <Toolbar>
        <ToolbarField label='Сотрудник'>
          <span className='flex items-center gap-3'>
            {имя !== null ? (
              <span className='max-w-72 truncate'>{имя}</span>
            ) : документ.isError ? (
              <span className='text-text-sub-600'>{человекаНет ? 'не найден' : 'не загружен'}</span>
            ) : (
              <Skeleton className='h-4 w-40' />
            )}
            <LinkButton.Root variant='primary' size='small' underline asChild>
              <Link to={АДРЕС_ВЫБОРА} className='цель-тача relative'>
                <LinkButton.Icon as={RiArrowLeftRightLine} />
                Другой человек
              </Link>
            </LinkButton.Root>
          </span>
        </ToolbarField>
        {календариВидны && (
          // Один период из двух границ: переносятся на вторую строку вместе, а не по одной.
          <div className='flex flex-wrap items-end gap-x-5 gap-y-3'>
            <PeriodStepper
              label='Период с'
              ariaName='начала периода'
              title={от === null ? null : formatPeriodTitle(от)}
              onPrev={() => сдвинуть('от', -1)}
              onNext={() => сдвинуть('от', 1)}
              nextDisabledReason={причинаВперёдОт}
            />
            <PeriodStepper
              label='Период по'
              ariaName='конца периода'
              title={до === null ? null : formatPeriodTitle(до)}
              onPrev={() => сдвинуть('до', -1)}
              onNext={() => сдвинуть('до', 1)}
              nextDisabledReason={причинаВперёдДо}
            />
          </div>
        )}
      </Toolbar>

      {запрет !== null && (
        // Объяснение, а не тишина: нажатие на стрелку, которая ничего не меняет, читается как
        // поломка. Стоит под шапкой и исчезает при следующем удавшемся шаге.
        <Hint.Root
          hasError
          role='status'
          className='border-b border-stroke-soft-200 bg-bg-white-0 px-5 py-2 узко:px-4'
        >
          <Hint.Icon as={RiErrorWarningLine} />
          {запрет}
        </Hint.Root>
      )}

      <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
        {человекаНет ? (
          <ЧеловекаНет />
        ) : (
          <ScreenState
            data={данные}
            error={документ.error}
            subject='расчёт сотрудника'
            isEmpty={(д) => д.blocks.length === 0 && д.gaps.length === 0}
            onRetry={() => void документ.refetch()}
            loading={<СкелетДокумента />}
            empty={данные === undefined ? null : <ПустойРасчёт данные={данные} />}
          >
            {(д) => (
              <div
                // Прежний документ остаётся, пока едет новый, но приглушён: числа не мигают
                // скелетом, и их не принять за числа нового периода.
                aria-busy={документ.isPlaceholderData}
                className={cn(
                  'flex flex-col transition-opacity',
                  документ.isPlaceholderData && 'opacity-60',
                )}
              >
                {документ.isError && <НеОбновилось onRetry={() => void документ.refetch()} />}
                <Документ данные={д} />
              </div>
            )}
          </ScreenState>
        )}
      </div>
    </main>
  );
}

/** Почему шаг вперёд у этой границы не делается; `undefined` — делается. */
function причина(
  от: Period | null,
  до: Period | null,
  какая: Граница,
  сегодня: Period,
): string | undefined {
  if (от === null || до === null) {
    return undefined;
  }
  const итог = шагГраницы(какая, 1, от, до, сегодня);
  return 'запрет' in итог ? итог.запрет : undefined;
}

/** Скелет документа: заголовок, итог и таблица. Виден только пока данных нет даже в кэше. */
function СкелетДокумента() {
  const [долго, setДолго] = useState(false);
  useEffect(() => {
    const таймер = window.setTimeout(() => setДолго(true), ДОЛГО_МС);
    return () => window.clearTimeout(таймер);
  }, []);
  return (
    <div role='status' aria-label='Считаем расчёт' className='flex flex-col gap-4 p-5 узко:p-4'>
      <Skeleton className='h-6 w-2/3' />
      <Skeleton className='h-44 w-full' />
      <GridSkeleton rows={6} band={false} />
      {долго && (
        <p className='text-paragraph-sm text-text-sub-600'>
          Связь медленная: расчёт ещё едет. Подождите немного или вернитесь к нему позже.
        </p>
      )}
    </div>
  );
}

/** Документ уже был на экране, а обновить его не удалось: остаётся последний, и это сказано. */
function НеОбновилось({ onRetry }: { onRetry: () => void }) {
  return (
    <div className='px-5 pt-4 узко:px-4'>
      <Alert.Root variant='lighter' status='warning' size='small' role='alert'>
        <Alert.Icon as={RiErrorWarningLine} />
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
          <span>
            Не удалось обновить расчёт: ниже последний полученный, числа могли измениться.
          </span>
          <Button.Root variant='neutral' mode='stroke' size='xsmall' onClick={onRetry}>
            Повторить
          </Button.Root>
        </div>
      </Alert.Root>
    </div>
  );
}

function ПустойРасчёт({ данные }: { данные: PayslipPeriodOut }) {
  return (
    <div className='p-5'>
      <EmptyState
        icon={RiCalculatorLine}
        title={`Считать пока нечего: ${данные.person.name}`}
        description={
          данные.empty_reason ??
          'За выбранные месяцы у человека нет ни ведомостей, ни назначений на объекты.'
        }
        action={
          <>
            <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
              <Link to='/payroll/sheets'>Открыть ведомости</Link>
            </Button.Root>
            <LinkButton.Root variant='primary' size='small' underline asChild>
              <Link to={АДРЕС_ВЫБОРА}>Выбрать другого человека</Link>
            </LinkButton.Root>
          </>
        }
      />
    </div>
  );
}

function ЧеловекаНет() {
  return (
    <div className='p-5'>
      <EmptyState
        icon={RiUserUnfollowLine}
        title='Такого человека нет или он вам не виден'
        description='Расчёт открывается из карточки человека или из списка на этой вкладке. Возможно, ссылка устарела или человек не на ваших объектах.'
        action={
          <Button.Root variant='neutral' mode='stroke' size='xsmall' asChild>
            <Link to={АДРЕС_ВЫБОРА}>Выбрать человека</Link>
          </Button.Root>
        }
      />
    </div>
  );
}

function НетПрава() {
  return (
    <main className='flex min-h-0 min-w-0 flex-1 items-center justify-center bg-bg-weak-50 p-5'>
      <EmptyState
        icon={RiLockLine}
        title='Расчёт сотрудника вам не показывается'
        description='Расчёт собирается из ведомостей, поэтому его видят те, кто видит ведомости: финансовый отдел, руководство, операционное руководство и администратор. Табель и карточки людей при этом могут быть доступны: это разные права.'
        action={
          <span className='text-paragraph-sm text-text-sub-600'>
            Нужен по работе: попросите администратора добавить право.
          </span>
        }
      />
    </main>
  );
}
